import { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import { FormControl, Grid } from '@mui/material';
import usePlanStore from '../../../../../../../../../store/planStore';
import { Advertiser } from '../../interfaces';
import MultiSelectAutocomplete from '../../../../../../../../../shared/components/MultiSelectAutocomplete/MultiSelectAutocomplete';

interface AdvertisersMultiselectProps {
  copyIndex: number;
  fetchedAdvertisers: Advertiser[];
  isAdvertisersFetching: boolean;
}

export default function AdvertisersMultiselect({
  copyIndex,
  fetchedAdvertisers,
  isAdvertisersFetching,
}: AdvertisersMultiselectProps) {
  const { t } = useTranslation();

  const {
    selectedCountry,
    updateCopyFormField,
    countryForms,
    advertisers,
    additionalAdvertisers,
  } = usePlanStore(
    (state) => ({
      countryForms: state.countryForms,
      selectedCountry: state.selectedCountry,
      updateCopyFormField: state.updateCopyFormField,
      additionalAdvertisers:
        state.countryForms[state.selectedCountry!]?.offline?.tv?.copyForm[
          copyIndex
        ].additionalAdvertisers,
      advertisers:
        state.countryForms[state.selectedCountry!]?.offline?.tv?.copyForm[
          copyIndex
        ]?.advertisers || [],
    }),
    shallow
  );

  const formData =
    countryForms[selectedCountry!].offline?.tv?.copyForm[copyIndex];

  const handleChangeStateValues = useCallback(
    (newValue: { id: string; title: string }[]) => {
      if (!selectedCountry || !advertisers) return;

      const newAdvertisersString =
        newValue.length > 0 ? newValue.map((adv) => adv.id).join('-') : '0';

      const updatedCopyForm =
        countryForms[selectedCountry!]?.offline?.tv?.copyForm || [];

      const currentIndex = updatedCopyForm.findIndex(
        (entry) => entry.id === formData?.id
      );

      const idParts = formData?.id ? formData.id.split('_') : [];

      if (idParts.length > 2) {
        if (!idParts[3] || idParts[3] !== String(currentIndex)) {
          idParts[3] = String(currentIndex);
        }

        idParts[2] = newAdvertisersString;
      }

      const updatedId = idParts.join('_');

      updateCopyFormField(
        selectedCountry,
        currentIndex,
        'advertisers',
        newValue
      );
      updateCopyFormField(selectedCountry, currentIndex, 'id', updatedId);
    },
    [
      selectedCountry,
      updateCopyFormField,
      advertisers,
      formData?.id,
      countryForms,
    ]
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <MultiSelectAutocomplete
            options={fetchedAdvertisers}
            id="advertisersMultiSelect"
            label={t('mediaPlanning.advertisers')}
            noOptionsText={t('mediaPlanning.noAdvertisers')}
            onChange={(
              _: ChangeEvent,
              newValue: { id: string; title: string }[]
            ) => {
              handleChangeStateValues(newValue);
            }}
            value={advertisers || []}
            limitTags={1}
            multiple
            disableCloseOnSelect
            clearOnBlur={false}
            disableClearable={false}
            disabled={isAdvertisersFetching}
            isLoading={isAdvertisersFetching}
            error={
              additionalAdvertisers
                ? +additionalAdvertisers !== advertisers.length
                : false
            }
            helperText={
              additionalAdvertisers &&
              +additionalAdvertisers !== advertisers.length
                ? "Doesn't match additional advertisers count"
                : ''
            }
          />
        </FormControl>
      </Grid>
    </Grid>
  );
}
